import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { SupabaseService } from '../supabase/supabase';
import { doSupabaseCall } from '../../shared/helpers/supabase.helper';
import type { IIndicatorItem, IIndicatorsVersion, IndicatorKind } from '../../shared/models';

const COLUMNS = 'id, codice, versione, stato, indicators';

interface IRow {
  id: string;
  codice: IndicatorKind | null;
  versione: number | null;
  stato: 'bozza' | 'attivo' | 'ritirato' | null;
  indicators: IIndicatorItem[];
}

// Registro degli strumenti (IIP/IBAP oggi) su Supabase: una riga = una versione di UN
// strumento (§2 doc DEV_v7). Append-only + trigger DB (0013): l'INSERT assegna la versione e
// ritira l'attiva precedente dello stesso codice, l'UPDATE ammette solo transizioni di stato.
@Injectable({ providedIn: 'root' })
export class IndicatorsService {
  // --- private ---
  private readonly _supabase: SupabaseService = inject(SupabaseService);

  // --- public ---
  // Versione attiva oggi per questo strumento: quella da proporre per una NUOVA compilazione.
  public getActiveVersion(codice: IndicatorKind): Observable<IIndicatorsVersion | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<IRow | null>(
      client
        .from('app_indicators')
        .select(COLUMNS)
        .eq('codice', codice)
        .eq('stato', 'attivo')
        .maybeSingle(),
    ).pipe(map((row) => (row ? this._toVersion(row) : null)));
  }

  // Ultima versione scritta per questo strumento (bozza o attivo, qualunque sia più recente):
  // quella da mostrare in Admin per continuare a modificarla.
  public getLatestVersion(codice: IndicatorKind): Observable<IIndicatorsVersion | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<IRow[]>(
      client
        .from('app_indicators')
        .select(COLUMNS)
        .eq('codice', codice)
        .order('id', { ascending: false })
        .limit(1)
        .returns<IRow[]>(),
    ).pipe(map((rows) => (rows[0] ? this._toVersion(rows[0]) : null)));
  }

  // Versione storica esatta (usata per ricompilare/rivedere una rilevazione con gli item
  // così com'erano al momento della compilazione, anche se admin li ha poi modificati).
  public getVersionById(id: string): Observable<IIndicatorsVersion | null> {
    const client = this._supabase.client;
    if (null === client) {
      return of(null);
    }
    return doSupabaseCall<IRow | null>(
      client.from('app_indicators').select(COLUMNS).eq('id', id).maybeSingle(),
    ).pipe(map((row) => (row ? this._toVersion(row) : null)));
  }

  // Pubblica una nuova versione attiva per lo strumento: il trigger DB assegna la versione e
  // ritira l'eventuale attiva precedente dello stesso codice, quindi qui basta un INSERT.
  public publishNew(codice: IndicatorKind, items: IIndicatorItem[]): Observable<void> {
    const client = this._supabase.client;
    if (null === client) {
      return of(undefined);
    }
    return doSupabaseCall(
      client.from('app_indicators').insert({ codice, stato: 'attivo', indicators: items }),
    ).pipe(map(() => undefined));
  }

  // Ritira una versione attiva senza pubblicarne una nuova (es. strumento non più in uso):
  // dopo, nessuna versione di quel codice risulta attiva finché non se ne pubblica una nuova.
  public retire(id: string): Observable<void> {
    const client = this._supabase.client;
    if (null === client) {
      return of(undefined);
    }
    return doSupabaseCall(
      client.from('app_indicators').update({ stato: 'ritirato', date_upd: new Date().toISOString() }).eq('id', id),
    ).pipe(map(() => undefined));
  }

  // Compatibilità con righe salvate prima che gli item diventassero { text, active } (erano
  // semplici string[]) o prima dell'aggiunta di dimensione/area/componente/textC/punteggio.
  private _toVersion(row: IRow): IIndicatorsVersion | null {
    if (null === row.codice || null === row.versione || null === row.stato) {
      return null; // riga legacy (blob combinato pre-0013): fuori dal nuovo registro per strumento.
    }
    const fix = (items: (string | IIndicatorItem)[]): IIndicatorItem[] =>
      items.map((it) => {
        const item = 'string' === typeof it ? { text: it, active: true } : it;
        return { dimensione: '', area: '', textC: '', punteggio: 'crescente' as const, ...item };
      });
    return {
      id: row.id,
      codice: row.codice,
      versione: row.versione,
      stato: row.stato,
      indicators: fix(row.indicators),
    };
  }
}
